import { closeSnackbar } from 'notistack';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSearchParams, useNavigate } from 'react-router-dom';
import CurrencyItem from '../../../../components/CurrencyItem';
import Input from '../../../../components/Input';
import AppConfig from '../../../../config';
import { t } from '../../../../lang';
import HelperService from '../../../../services/HelperService';
import { sendSlice } from '../../../../store/slices';

import './BuyBillingAsset.scss';

const { routes } = AppConfig;

const CREDIT_CURRENCY = AppConfig?.creditCurrency ?? 'Credits';

const BuyBillingAsset = () => {
	const [noAmountError, setNoAmountError] = useState(false);

	const notEnoughNotification = useRef(null);

	const amount = useSelector(store => store.send.amount);
	const availableCurrencies = useSelector(store => store.app.availableCurrencies);
	const quotes = useSelector(store => store.app.quotes);

	const navigate = useNavigate();
	const dispatch = useDispatch();

	const filteredCurrencies = availableCurrencies.filter(currency => currency.Symbol === 'USDT-TRC20');
	const sortedCurrencies = [...filteredCurrencies].sort((a, b) => b.AmountUsd - a.AmountUsd);
	const billingRate = quotes.find(quote => quote.Symbol === 'Credits')?.Rate || 0;
	const amountUsd = billingRate * amount;
	const notEnoughBalance = amountUsd > sortedCurrencies[0]?.AmountUsd;

	const removeNotEnoughNotification = () => {
		closeSnackbar(notEnoughNotification.current);
		notEnoughNotification.current = null;
	};

	useEffect(() => {
		dispatch(sendSlice.actions.setAmount(''));

		return () => {
			if (notEnoughNotification.current) removeNotEnoughNotification();
		};
	}, []);

	const updateAmount = e => {
		setNoAmountError(false);

		const value = e.target.value;
		const decimalAmount = String(value).replace(',', '.');
		const filteredAmount = decimalAmount.replace(/[^.\d]/g, '');
		const onlyOneDotAmount = filteredAmount.replace(/(?<!^[^.]*)\./g, '');
		dispatch(sendSlice.actions.setAmount(onlyOneDotAmount));

		const numberAmount = Number(onlyOneDotAmount);
		const amountUsd = billingRate * numberAmount;
		const notEnough = amountUsd > sortedCurrencies[0].AmountUsd;

		if (notEnough && !notEnoughNotification.current) {
			notEnoughNotification.current = HelperService.showNotification({
				className: 'BuyBillngAsset-NotEnough',
				icon: 'EXCLAMATIONMARK_CIRCLE_FILL',
				title: t('Attention'),
				description: t('Not enough balance'),
				iconSize: 32,
				persist: true,
				visibleClose: true,
				autoHideDuration: 0,
				onClose: removeNotEnoughNotification,
			});
		} else if (!notEnough && notEnoughNotification.current) {
			removeNotEnoughNotification();
		}
	};

	const onAmountBlur = e => {
		const value = e.target.value;
		const correctedAmount =
			value.split('.')[0].replace(/^0+(\d+)$/, '$1') + (value.includes('.') ? value.slice(value.indexOf('.')) : '');
		const formatRegExp = new RegExp('(\\.[^.]{' + HelperService.GetCurrencyPrecisionByName(CREDIT_CURRENCY) + '}).*');
		const cuttedAmount = correctedAmount.replace(formatRegExp, '$1');
		const numberValue = Number(cuttedAmount);
		dispatch(sendSlice.actions.setAmount(String(numberValue)));
	};

	const selectAsset = asset => {
		if (!Number(amount)) {
			setNoAmountError(true);
			return;
		}

		if (asset.AmountUsd >= amountUsd) {
			navigate({ pathname: routes.buyBillingAddress, search: createSearchParams({ currencyId: asset.Id }).toString() });
		} else {
			HelperService.showNotification({
				className: 'BuyBillngAsset-NotEnough',
				icon: 'EXCLAMATIONMARK_CIRCLE_FILL',
				title: t('Attention'),
				description: t('Not enough balance'),
				iconSize: 32,
			});
		}
	};

	return (
		<div className='BuyBillingAsset'>
			<div className='BuyBillingAsset__amount-box'>
				<div className='BuyBillingAsset__field-header'>{t('Amount_v2')}</div>
				<Input
					className={
						'BuyBillingAsset__amount FormBlock' + (notEnoughBalance ? ' BuyBillingAsset__amount-not-enough' : '')
					}
					placeholder='0'
					value={amount}
					onChange={updateAmount}
					onBlur={onAmountBlur}
					postfix={CREDIT_CURRENCY}
					inputMode='decimal'
					maxLength={16}
				/>
				{noAmountError && <div className='BuyBillingAsset__amount-error'>{t('Enter amount')}</div>}
			</div>

			<div className='BuyBillingAsset__field-header'>{t('Select asset to pay')}</div>
			<div className='BuyBillingAddress__currencies-box'>
				<div className='FormBlock'>
					{sortedCurrencies.map((currency, index) => (
						<CurrencyItem
							className={amountUsd > currency.AmountUsd ? 'BuyBillngAsset__block-asset' : ''}
							key={index}
							currency={currency}
							lastItem={index === sortedCurrencies.length - 1}
							onClick={selectAsset}
						/>
					))}
				</div>
			</div>
		</div>
	);
};

export default BuyBillingAsset;
